import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import { Beneficiary, County, SearchResultPlan } from "../../../@types";
import {
  CurrentCoverageCard,
  EnrollmentConfirmationCard,
  FindPlansCard,
  FutureCoverageCard,
  RolloverCoverageCard,
  YearOverYearCompareCard,
} from "./plan-card-types";
import { AppContext } from "../../../app/store";
import { CONSTANTS, IntlContext, useTranslate } from "../../../helpers";
import { BedapDownAlert } from "../components";
import { useFlags } from "launchdarkly-react-client-sdk";
import BasePlanCard from "./plan-card-types/BasePlanCard";
import { getYearPartInfo } from "../../../helpers/yearPartHelpers";
import { AnalyticsActionType } from "../../../app/contexts/Analytics/types";

interface PlanCardsContainerProps {
  beneficiary: Beneficiary;
  county: County | undefined;
  currentCoverage: SearchResultPlan | undefined;
  nextYearCoverage: SearchResultPlan | undefined;
  isBEDAPDown: boolean;
  onFindPlansClick: () => void;
  overThirtyDaysSinceLogin?: boolean;
  zipcode: string | undefined;
}

export const yearOverYearCompareCardTestId = "year-over-year-compare-card";
export const multipleRolloverPlansCardTestId = "multiple-rollover-plans-card";

const PlanCardsContainer: FunctionComponent<PlanCardsContainerProps> = ({
  beneficiary,
  county,
  currentCoverage,
  isBEDAPDown,
  nextYearCoverage,
  onFindPlansClick,
  overThirtyDaysSinceLogin = false,
  zipcode,
}) => {
  const {
    state: { language, oecStatuses },
    state,
    dispatch,
  } = useContext(AppContext);
  const futurePlan = beneficiary.future_coverage[0];
  const flags = useFlags();
  const { isOutsideOpenEnrollment } = getYearPartInfo(flags);
  const t = useTranslate();
  const fm = useContext(IntlContext).formatMessage;
  const [shouldShowRolloverPlanCard, setShouldShowRolloverPlanCard] = useState(
    !isOutsideOpenEnrollment &&
      Boolean(nextYearCoverage) &&
      !isBEDAPDown &&
      Boolean(!futurePlan)
  );

  useEffect(() => {
    if (!futurePlan) {
      setShouldShowRolloverPlanCard(
        !isOutsideOpenEnrollment &&
          Boolean(nextYearCoverage) &&
          !isBEDAPDown &&
          !oecStatuses.currentYear &&
          !oecStatuses.nextYear
      );
    }
  }, [
    futurePlan,
    isOutsideOpenEnrollment,
    nextYearCoverage,
    oecStatuses.currentYear,
    oecStatuses.nextYear,
    isBEDAPDown,
  ]);

  const shouldShowMultipleRolloverPlansCard =
    !isOutsideOpenEnrollment && state.multipleRolloverPlans && !isBEDAPDown;

  const shouldShowYearOverYearCard =
    !isOutsideOpenEnrollment &&
    currentCoverage &&
    nextYearCoverage &&
    !futurePlan &&
    !oecStatuses.currentYear &&
    !oecStatuses.nextYear &&
    !overThirtyDaysSinceLogin &&
    !isBEDAPDown;

  return (
    <div>
      <ul
        className="mct-c-plan-cards-container"
        role="list"
        aria-labelledby="YourPlansHeading"
      >
        {isBEDAPDown ? (
          <li>
            <BedapDownAlert region="plans" />
          </li>
        ) : (
          <>
            <li role="listitem">
              <CurrentCoverageCard
                {...{
                  isOutsideOpenEnrollment,
                  plan: currentCoverage,
                  language,
                }}
              />
            </li>
            {oecStatuses.currentYear && (
              <li role="listitem">
                <EnrollmentConfirmationCard
                  {...{
                    isOutsideOpenEnrollment,
                    oecStatus: oecStatuses.currentYear,
                    language,
                  }}
                />
              </li>
            )}
            {futurePlan && (
              <li role="listitem">
                <FutureCoverageCard
                  {...{ isOutsideOpenEnrollment, futurePlan, language }}
                />
              </li>
            )}
            {oecStatuses.nextYear && (
              <li role="listitem">
                <EnrollmentConfirmationCard
                  {...{
                    isOutsideOpenEnrollment,
                    oecStatus: oecStatuses.nextYear,
                    language,
                  }}
                />
              </li>
            )}
          </>
        )}

        {shouldShowRolloverPlanCard && (
          <li role="listitem">
            <RolloverCoverageCard
              plan={nextYearCoverage as SearchResultPlan}
              language={language}
            />
          </li>
        )}

        {shouldShowMultipleRolloverPlansCard && (
          <li role="listitem">
            <BasePlanCard
              dataTestId={multipleRolloverPlansCardTestId}
              header={fm(
                { id: "yoy.multiple_rollover.plan" },
                { year: flags.mctCurrentPlanYear + 1 }
              )}
              subHeader={fm(
                { id: "yoy.multiple_rollover.contact_plan" },
                { year: flags.mctCurrentPlanYear + 1 }
              )}
            />
          </li>
        )}

        {shouldShowYearOverYearCard && (
          <li role="listitem">
            <YearOverYearCompareCard
              dataTestId={yearOverYearCompareCardTestId}
              currentCoverage={currentCoverage as SearchResultPlan}
              nextYearCoverage={nextYearCoverage as SearchResultPlan}
            />
          </li>
        )}

        {!overThirtyDaysSinceLogin && (
          <li role="listitem">
            <FindPlansCard
              {...{
                county: county?.name,
                state: county?.state,
                zipcode,
                mailingAddress: beneficiary.mailing_address,
                currentCoverage,
                onFindPlansClick,
              }}
            />
          </li>
        )}
      </ul>
      <div className="mct-c-have-other-medicare-coverage mct-hide-print">
        <h3 className="ds-text-heading--lg mct-c-have-other-medicare-coverage__header">
          {t("summary_page.other_medicare_coverage.header")}
        </h3>
        <a
          href={CONSTANTS.linkToMyPlans(language)}
          onClick={() =>
            dispatch({
              type: AnalyticsActionType.SEND_INTERNAL_LINK_CLICKED_EVENT,
              settings: {
                linkText: t(
                  "summary_page.links.get_details_about_your_other_coverage"
                ),
                linkUrl: CONSTANTS.linkToMyPlans(language),
              },
            })
          }
        >
          {t("summary_page.links.get_details_about_your_other_coverage")}
        </a>
      </div>
    </div>
  );
};

export default PlanCardsContainer;
